import type { DragEndEvent } from '@dnd-kit/core';
import { closestCenter, DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Box, Text } from '@timelyapp/react';
import {
  COLUMN_BILLABLE,
  COLUMN_CLIENT,
  COLUMN_DAY,
  COLUMN_ESTIMATED_HOURS,
  COLUMN_ESTIMATED_MONEY,
  COLUMN_LINKED_WORK_ID,
  COLUMN_LINKED_WORK_STATUS,
  COLUMN_LINKED_WORK_TITLE,
  COLUMN_LOGGED_HOURS,
  COLUMN_LOGGED_MONEY,
  COLUMN_NAME,
  COLUMN_NOTES,
  COLUMN_PROJECT,
  COLUMN_STATE,
  COLUMN_TAGS,
} from 'constants/filters';
import React, { useCallback, useMemo } from 'react';
import styles from './DatatableColumnOptions.module.scss';
import SortableColumnOption from './SortableColumnOption';

// Default labels for timesheets datatable columns
const TIMESHEETS_COLUMN_LABELS: Record<string, string> = {
  [COLUMN_BILLABLE]: 'Billable',
  [COLUMN_DAY]: 'Date',
  [COLUMN_NAME]: 'User',
  [COLUMN_PROJECT]: 'Project',
  [COLUMN_TAGS]: 'Tags',
  [COLUMN_CLIENT]: 'Client',
  [COLUMN_STATE]: 'State',
  [COLUMN_LOGGED_HOURS]: 'Time',
  [COLUMN_LOGGED_MONEY]: 'Money',
  [COLUMN_ESTIMATED_HOURS]: 'Planned time',
  [COLUMN_ESTIMATED_MONEY]: 'Planned money',
  [COLUMN_NOTES]: 'Note',
  [COLUMN_LINKED_WORK_ID]: 'Linked Work Item ID',
  [COLUMN_LINKED_WORK_TITLE]: 'Linked Work Item Title',
  [COLUMN_LINKED_WORK_STATUS]: 'Linked Work Item Sync Status',
  num_of_projects: 'Projects',
  num_of_users: 'Users',
  total: 'Total',
};

interface Props {
  columnOrder: Array<string>;
  columnVisibility: Record<string, boolean>;
  onColumnOrderChange: React.Dispatch<React.SetStateAction<Array<string>>>;
  onColumnVisibilityChange: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  /** Column ID → display label map. Defaults to timesheets column labels. */
  columnLabels?: Record<string, string>;
}

const DatatableColumnOptions: React.FC<Props> = ({
  columnOrder,
  columnVisibility,
  onColumnOrderChange,
  onColumnVisibilityChange,
  columnLabels = TIMESHEETS_COLUMN_LABELS,
}) => {
  // Filter to only columns that have labels - this list must match what's rendered
  // to ensure drag-and-drop positioning works correctly
  const sortableColumns = useMemo(
    () => columnOrder.filter(c => !!columnLabels[c]),
    [columnOrder, columnLabels]
  );

  const handleColumnVisibilityChange = useCallback(
    (columnId: string, isVisible: boolean) => {
      onColumnVisibilityChange(visibility => ({
        ...visibility,
        [columnId]: isVisible,
      }));
    },
    [onColumnVisibilityChange]
  );

  const handleMoveColumn = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (active.id === over?.id) return;

      const oldIndex = columnOrder.indexOf(active.id as string);
      const newIndex = columnOrder.indexOf(over?.id as string);

      const updatedOrder = arrayMove(columnOrder, oldIndex, newIndex);
      onColumnOrderChange(updatedOrder);
    },
    [columnOrder, onColumnOrderChange]
  );

  return (
    <Box.Column className={styles.inner}>
      <Box.Row className={styles.sectionTitle}>
        <Text.Label size="xsmall" color="subtle">
          Column visibility and order
        </Text.Label>
      </Box.Row>
      <Box.Column className={styles.columns}>
        <DndContext
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleMoveColumn}
        >
          <SortableContext items={sortableColumns} strategy={verticalListSortingStrategy}>
            {sortableColumns.map(columnId => (
              <SortableColumnOption
                key={columnId}
                columnId={columnId}
                label={columnLabels[columnId]}
                isVisible={!!columnVisibility[columnId]}
                onColumnVisibilityChange={handleColumnVisibilityChange}
              />
            ))}
          </SortableContext>
        </DndContext>
      </Box.Column>
    </Box.Column>
  );
};

export default DatatableColumnOptions;
